import { classifyApiError, type ApiFailure } from './errors';

/**
 * How route loaders return a failure (P0-WEB-04).
 *
 * Loaders are the portal's data layer, and the obvious thing — let the loader
 * throw and let `errorElement` catch it — gives every failure on a route the
 * same appearance. PRD §15 asks for the opposite: "every one of these must be
 * designed, not left to a generic toast", and a shared error element is a
 * generic toast with a larger font.
 *
 * So a failure is data. The loader resolves either way, the route component
 * gets a discriminated result, and it decides whether an empty imagery search
 * looks like a rate limit. The `errorElement` stays for what it is actually
 * for: a lazy chunk that would not download, or a bug in the loader itself.
 */
export type LoadResult<T> =
  { readonly ok: true; readonly data: T } | { readonly ok: false; readonly failure: ApiFailure };

/**
 * Runs a request and classifies whatever it throws.
 *
 * `cancelled` is rethrown rather than returned. React Router aborts a loader's
 * signal when the user navigates away mid-load, and returning that as a failure
 * would render "the request was stopped" on a page the user has already left.
 *
 * A `401` comes back as `kind: 'unauthenticated'` rather than a redirect,
 * because P0-WEB-04 has no sign-in route to redirect to. P0-WEB-07 wraps this
 * with the protected-route loader that turns that one kind into
 * `throw redirect(...)`; the client's `onUnauthenticated` port already handles
 * clearing the session, which is the part that must not wait.
 */
export async function loadFrom<T>(load: () => Promise<T>): Promise<LoadResult<T>> {
  try {
    return { ok: true, data: await load() };
  } catch (error) {
    const failure = classifyApiError(error);
    if (failure.kind === 'cancelled') throw error;
    return { ok: false, failure };
  }
}
